import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { wachtwoordVergeten } from '../api/authApi';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { loginUser, logoutUser, isLoading, error: storeError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetOk, setResetOk] = useState(false);

  const verstuurReset = async () => {
    if (!resetEmail.includes('@')) return;
    setResetBusy(true);
    try { await wachtwoordVergeten(resetEmail); setResetOk(true); }
    finally { setResetBusy(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Vul uw e-mailadres en wachtwoord in');
      return;
    }

    try {
      // Backoffice = medewerkerslogin (geen collectiviteit).
      await loginUser({ email, password, collectivity_id: undefined });
      const role = useAuthStore.getState().user?.role;
      if (role !== 'kva_admin') {
        await logoutUser();
        setError('Dit account heeft geen toegang tot de backoffice.');
        return;
      }
      navigate('/backoffice/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inloggen mislukt');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/logo-mijnfactuurverzekerd.svg"
            alt="MijnFactuurVerzekerd"
            className="h-16 w-auto mx-auto mb-4"
          />
          <p className="text-mfb-blue font-sans text-sm tracking-wide uppercase tracking-widest">
            Backoffice
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <h2 className="font-display text-2xl font-semibold text-mfb-blue mb-6 text-center">
            Inloggen
          </h2>

          {(error || storeError) && (
            <div className="mb-6 p-4 bg-mfb-orange/10 border border-mfb-orange/30 rounded-lg">
              <p className="text-sm text-mfb-orange-dark">{error || storeError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                E-mailadres
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="naam@bedrijf.nl"
                autoComplete="username"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue focus:border-transparent outline-none transition"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Wachtwoord
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue focus:border-transparent outline-none transition"
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 bg-mfb-orange hover:bg-mfb-orange-dark disabled:bg-slate-400 text-white font-semibold rounded-lg transition shadow-sm"
            >
              {isLoading ? 'Bezig met inloggen...' : 'Inloggen'}
            </button>
          </form>

          <div className="mt-4 text-center">
            {!resetOpen ? (
              <button type="button" onClick={() => setResetOpen(true)} className="text-sm text-slate-500 hover:text-mfb-blue hover:underline">
                Wachtwoord vergeten?
              </button>
            ) : resetOk ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 text-left">
                Als er een account bij dit e-mailadres hoort, is er een reset-link verstuurd. De link is 1 uur geldig.
              </div>
            ) : (
              <div className="p-4 border border-slate-200 rounded-lg text-left space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-mfb-blue">Wachtwoord vergeten</span>
                  <button type="button" onClick={() => setResetOpen(false)} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
                </div>
                <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="naam@bedrijf.nl"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue focus:border-transparent outline-none transition" />
                <button type="button" onClick={verstuurReset} disabled={resetBusy}
                  className="w-full py-2 px-4 bg-mfb-blue hover:bg-mfb-blue/90 disabled:bg-slate-400 text-white font-semibold rounded-lg transition text-sm">
                  {resetBusy ? 'Versturen…' : 'Verstuur reset-link'}
                </button>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Uitsluitend voor medewerkers van MijnFactuurVerzekerd / FourSure.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
